import uiI18n from "../i18n";
import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import {
  LuBell,
  LuCalendarDays,
  LuChevronDown,
  LuCircleUserRound,
  LuEye,
  LuHeart,
  LuHistory,
  LuHouse,
  LuLogIn,
  LuLogOut,
  LuMessageCircleMore,
  LuSearchCheck,
  LuShieldCheck,
  LuUserPlus,
  LuUserRound,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { useTranslation } from "react-i18next";
import useAdminAccess from "../hooks/useAdminAccess";

const ITEM_CLASS = "group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-medium text-slate-700 transition hover:bg-green-50 hover:text-green-700";
const ICON_CLASS = "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition group-hover:bg-green-100 group-hover:text-green-600";

// Administration entry: server-verified role, mounted only while the account menu is open.
function AdminEntry({ itemRole, onSelect }) {
  const { allowed } = useAdminAccess();
  const { t } = useTranslation();
  if (!allowed) return null;
  return (
    <button type="button" role={itemRole} onClick={() => onSelect("/admin")} className={ITEM_CLASS}>
      <span className={ICON_CLASS}><LuShieldCheck size={17} strokeWidth={1.8} /></span>
      <span className="flex-1">{t("administration.title")}</span>
    </button>
  );
}

export default function UserMenu({ open, onOpenChange, triggerContent } = {}) {
  const itemRole = "menuitem";
  const [localOpen, setLocalOpen] = useState(false);
  const isOpen = open ?? localOpen;
  const setIsOpen = onOpenChange ?? setLocalOpen;
  const [user, setUser] = useState(null);
  const [isMobile, setIsMobile] =
    useState(false);

  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const navigate = useNavigate();
  const auth = getAuth();
  const { t } = useTranslation();

  const displayName =
    user?.displayName?.trim() || "";

  const nameParts = displayName
    .split(" ")
    .filter(Boolean);

  const firstName = nameParts[0] || "";
  //const lastName = nameParts.slice(1).join(" ");

  const initials = displayName
    ? nameParts
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase()
    : "";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1280);
    };

    handleResize();

    window.addEventListener(
      "resize",
      handleResize
    );

    const unsubscribe = onAuthStateChanged(
      auth,
      (currentUser) => {
        setUser(currentUser);
      }
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );

      unsubscribe();
    };
  }, [auth]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener(
      "pointerdown",
      handleClickOutside
    );

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "pointerdown",
        handleClickOutside
      );

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [isOpen, setIsOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsOpen(false);
      navigate("/SignIn");
    } catch (error) {
      console.error(
        "Erreur lors de la déconnexion :",
        error
      );
    }
  };

  const connectedMenuItems = [
    {
      label: t("my_annonce"),
      icon: LuHouse,
      path: "/myAds",
    },
    // ...(isMobile
    //   ? [
    //       {
    //         label: t("my_message"),
    //         icon: LuMessageCircleMore,
    //         path: "/ConversationPage",
    //       },
    //     ]
    //   : []),
    {
      label: t("my_reservation"),
      icon: LuCalendarDays,
      path: "/AllReservation",
    },
    {
      label: t("my_profil"),
      icon: LuUserRound,
      path: "/profile",
    },
    {
      label: t("my_demande"),
      icon: LuBell,
      path: "/AllRequest",
    },
    // "Je recherche un bien" requests (personal space); distinct from "Recherches récentes".
    {
      label: t("propertyRequest.nav.mine"),
      icon: LuSearchCheck,
      path: "/my-property-requests",
    },
  ];

  // Below 1280 px the account menu is the single place for these shortcuts (signed in or visitor).
  const mobileShortcuts = isMobile ? [
    { label: t("messages"), icon: LuMessageCircleMore, path: "/ConversationPage" },
    { label: t("mobile.favorites"), icon: LuHeart, path: "/favorites" },
    { label: t("ui.recent_search_history"), icon: LuHistory, path: "/recent-searches" },
    { label: t("ui.recently_viewed"), icon: LuEye, path: "/recently-viewed" },
  ] : [];

  return (
    <div className="relative lina-user-control">
      <button
        ref={buttonRef}
        type="button"
        onClick={() =>
          setIsOpen(!isOpen)
        }
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={
          firstName
            ? uiI18n.t("ui.account_name", { name: firstName })
            : uiI18n.t("ui.user_account")
        }
        className="
          group
          inline-flex
          h-11
          items-center
          justify-center
          gap-2
          rounded-2xl
          border
          border-transparent
          px-2.5
          text-sm
          font-semibold
          text-slate-600
          transition-all
          duration-200
          hover:border-slate-200
          hover:bg-green-50
          hover:text-green-600
          focus:outline-none
          focus:ring-4
          focus:ring-green-500/15
        "
      >
        {triggerContent ?? <>
        {user && initials ? (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-700 text-xs font-black text-white shadow-sm">
            {initials}
          </span>
        ) : (
          <LuCircleUserRound
            size={23}
            strokeWidth={1.7}
            className="transition-transform group-hover:scale-110"
          />
        )}

        {user && firstName && (
          <span className="hidden max-w-24 truncate lg:inline">
            {firstName}
          </span>
        )}

        <LuChevronDown
          size={15}
          className={`transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
        </>}
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          role="menu"
          className={`
            absolute
            end-0
            top-full
            z-[90]
            mt-3
            w-64
            overflow-hidden
            rounded-2xl
            border
            border-slate-100
            bg-white
            p-2
            shadow-2xl
          `}
        >
          {user && (
            <div className="mb-2 border-b border-slate-100 px-3 pb-3 pt-2">
              <p className="truncate text-sm font-bold text-slate-900">
                {displayName ||
                  user.email ||
                  uiI18n.t("ui.user")}
              </p>

              <p className="mt-0.5 truncate text-xs text-slate-500">
                {user.email ||
                  uiI18n.t("ui.my_account")}
              </p>
            </div>
          )}

          {user ? (
            <>
              <div className="space-y-1">
                {[...connectedMenuItems, ...mobileShortcuts].map(
                  (item) => {
                    const Icon = item.icon;

                    return (
                      <button
                        key={item.path}
                        type="button"
                        role={itemRole}
                        onClick={() =>
                          handleNavigation(
                            item.path
                          )
                        }
                        className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-medium text-slate-700 transition hover:bg-green-50 hover:text-green-700"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition group-hover:bg-green-100 group-hover:text-green-600">
                          <Icon
                            size={17}
                            strokeWidth={1.8}
                          />
                        </span>

                        <span className="flex-1">
                          {item.label}
                        </span>
                      </button>
                    );
                  }
                )}
                {/* Mobile and desktop: shown only when the server confirms the Admin role (GET /api/admin/session). */}
                <AdminEntry itemRole={itemRole} onSelect={handleNavigation} />
              </div>

              <div className="my-2 h-px bg-slate-100" />

              <button
                type="button"
                role={itemRole}
                onClick={handleLogout}
                className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-medium text-red-600 transition hover:bg-red-50"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-50 text-red-500">
                  <LuLogOut
                    size={17}
                    strokeWidth={1.8}
                  />
                </span>

                <span>{t("logout")}</span>
              </button>
            </>
          ) : (
            <div className="space-y-1">
              <button
                type="button"
                role={itemRole}
                onClick={() =>
                  handleNavigation("/SignIn")
                }
                className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-medium text-slate-700 transition hover:bg-green-50 hover:text-green-700"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-green-50 text-green-600">
                  <LuLogIn
                    size={17}
                    strokeWidth={1.8}
                  />
                </span>

                <span>
                  {t("login") ||
                    uiI18n.t("ui.sign_in")}
                </span>
              </button>

              <button
                type="button"
                role={itemRole}
                onClick={() =>
                  handleNavigation("/SignUp")
                }
                className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-medium text-slate-700 transition hover:bg-green-50 hover:text-green-700"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
                  <LuUserPlus
                    size={17}
                    strokeWidth={1.8}
                  />
                </span>

                <span>
                  {t("signup") ||
                    uiI18n.t("ui.create_an_account")}
                </span>
              </button>

              {mobileShortcuts.length > 0 && <div className="my-2 h-px bg-slate-100" />}
              {mobileShortcuts.map(({ label, icon: Icon, path }) => (
                <button key={path} type="button" role={itemRole} onClick={() => handleNavigation(path)} className={ITEM_CLASS}>
                  <span className={ICON_CLASS}><Icon size={17} strokeWidth={1.8} /></span>
                  <span className="flex-1">{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
